import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './Icon';

interface Props {
  /** makes the whole row a link */
  to?: string;
  /** before the text (dot, step number) */
  lead?: ReactNode;
  title?: ReactNode;
  meta?: ReactNode;
  /** content at the top of the text column, before title and meta (a link, a header line) */
  children?: ReactNode;
  /** after the text (chip, link, time) */
  trailing?: ReactNode;
  className?: string;
  style?: CSSProperties;
  titleStyle?: CSSProperties;
  textStyle?: CSSProperties;
}

/** One row of a `.list`: optional lead, a title + meta text column, optional trailing content; a linked row without trailing content ends in a chevron. */
export function ListRow({ to, lead, title, meta, children, trailing, className = '', style, titleStyle, textStyle }: Props) {
  const cls = `list-row ${className}`.trim();
  const inner = (
    <>
      {lead}
      <div className="grow" style={textStyle}>
        {children}
        {title !== undefined && <span className="title" style={titleStyle}>{title}</span>}
        {meta !== undefined && <span className="meta">{meta}</span>}
      </div>
      {trailing}
      {/* a row that already ends in a chip or a link keeps that end; the chevron would only crowd it */}
      {to && trailing == null && (
        <span className="list-row-go" aria-hidden="true">
          <Icon name="next" size={16} />
        </span>
      )}
    </>
  );
  return to ? (
    <li>
      <Link to={to} className={cls} style={style}>
        {inner}
      </Link>
    </li>
  ) : (
    <li className={cls} style={style}>
      {inner}
    </li>
  );
}
